import { useLocalStorage, useSessionStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const useUIStore = defineStore('ui', () => {
  const menuMode = useLocalStorage('ECDMenuMode', 'normal')
  const isCapsLockOn = ref(false)

  const lastClientRoute = useSessionStorage('ECDLastClientRoute', null)

  const clientTabs = useSessionStorage('ECDClientTabs', [])

  const setCapsLockState = (state) => {
    isCapsLockOn.value = state
  }

  const checkCapsLockState = (event) => {
    if (event && typeof event.getModifierState !== 'undefined') {
      setCapsLockState(event.getModifierState('CapsLock'))
    }
  }

  let capsLockListenersActive = false

  const addCapsLockListeners = () => {
    if (capsLockListenersActive === true) {
      return () => {}
    }

    capsLockListenersActive = true

    window.addEventListener('keydown', checkCapsLockState)
    window.addEventListener('keyup', checkCapsLockState)

    return () => {
      window.removeEventListener('keydown', checkCapsLockState)
      window.removeEventListener('keyup', checkCapsLockState)
      capsLockListenersActive = false
    }
  }

  const getClientTabIndex = (clientId) => {
    const id = String(clientId)
    const tabs = Array.isArray(clientTabs.value) ? clientTabs.value : []
    return tabs.findIndex((c) => String(c?.id) === id)
  }

  const upsertClientTab = (client) => {
    if (!client || !client.id) {
      return null
    }

    const id = String(client.id)
    const tabs = Array.isArray(clientTabs.value) ? clientTabs.value : []
    const idx = getClientTabIndex(id)

    let entry

    if (idx === -1) {
      entry = {
        ...client,
        id,
        lastRoute: null,
      }
    } else {
      const existing = tabs[idx]
      entry = {
        ...existing,
        ...client,
        id,
        lastRoute: existing?.lastRoute ?? null,
      }
      tabs.splice(idx, 1)
    }

    tabs.unshift(entry)
    clientTabs.value = tabs
    return entry
  }

  const removeClientTab = (clientId) => {
    const id = String(clientId)
    const tabs = Array.isArray(clientTabs.value) ? clientTabs.value : []
    clientTabs.value = tabs.filter((c) => String(c?.id) !== id)
  }

  const resetClientTabs = () => {
    clientTabs.value = []
    lastClientRoute.value = null
  }

  const setLastRouteForClientId = (clientId, lastRoute) => {
    const id = String(clientId)
    const tabs = Array.isArray(clientTabs.value) ? clientTabs.value : []
    const idx = getClientTabIndex(id)

    if (idx === -1) {
      return
    }

    tabs[idx] = {
      ...tabs[idx],
      lastRoute,
    }

    clientTabs.value = tabs
  }

  const getClientTabById = (clientId) => {
    const id = String(clientId)
    const tabs = Array.isArray(clientTabs.value) ? clientTabs.value : []
    return tabs.find((c) => String(c?.id) === id) || null
  }

  const orderedClientTabs = computed(() => {
    return Array.isArray(clientTabs.value) ? clientTabs.value : []
  })

  return {
    menuMode,
    isCapsLockOn,

    lastClientRoute,

    clientTabs: orderedClientTabs,

    setCapsLockState,
    addCapsLockListeners,

    upsertClientTab,
    removeClientTab,
    setLastRouteForClientId,
    getClientTabById,
    resetClientTabs,
  }
})
